import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeChannel, userChannel } from '@/core/realtime';
import { useMyMemberPublicId } from '@/stores/session';
import { notificationKeys } from './queries';
import type { UnreadCountDto, UserChannelPublication } from './types';

/**
 * Bind the caller's own `user:` channel to the notification cache (FE-ADR-017).
 *
 * Two payload shapes arrive here (backend ADR-021):
 *  - `notification` — the badge nudge. When it carries a count we write it
 *    straight into the cache (no round-trip); when it does not (roster fan-out,
 *    where a shared payload cannot hold per-member counts) we invalidate and
 *    let the badge re-read it.
 *  - `announcement.created` — a new post the caller is entitled to; the feed
 *    and the badge are both refetched.
 *
 * Anything unrecognized is ignored: the server may add a payload type before
 * this client knows about it.
 */
export function useUserChannel(): void {
  const qc = useQueryClient();
  const memberPublicId = useMyMemberPublicId();

  const onPublication = useCallback(
    (payload: UserChannelPublication) => {
      if (payload?.type === 'notification') {
        if (typeof payload.unread_count === 'number') {
          qc.setQueryData<UnreadCountDto>(notificationKeys.unreadCount(), {
            unread: payload.unread_count,
          });
        } else {
          void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        }
        void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
        return;
      }
      if (payload?.type === 'announcement.created') {
        void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
        void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      }
    },
    [qc]
  );

  useRealtimeChannel<UserChannelPublication>(
    memberPublicId ? userChannel(memberPublicId) : null,
    onPublication
  );
}
